import React, {FC} from 'react';
import styled from 'styled-components';

import {filterValidInterval} from '../../../libs/shared/core/src/helpers/datetime/luxonHelpers';
import {Trans} from '../../../libs/shared/core/src/helpers/internationalization/react';
import {TeammateAvailabilityStatusesEnum} from '../../../libs/shared/core/src/models/api/teammateModel';
import {getTimeFormat} from '../../../libs/shared/core/src/store/data/preferences/commonPreferencesSelectors';
import {Toggle} from '../../../libs/shared/ui/src/components/booleanInputs/toggle';
import {VisualSizesEnum} from '../../../libs/shared/ui/src/styles/commonStyles';
import {fontSizes, fontWeights} from '../../../libs/shared/ui/src/styles/fontStyles';
import {center} from '../../../libs/shared/ui/src/styles/mixins';
import {useDoNotDisturb} from '../../../srcCommon/app/doNotDisturbHooks';
import {useAppDispatch, useAppSelector} from '../../helpers/appReduxHelpers';
import {
  updateDoNotDisturbAction,
  updateShouldMuteNotificationsAction,
} from '../../store/data/preferences/preferencesActions';
import {getDoNotDisturb, getShouldMuteNotifications} from '../../store/data/preferences/preferencesSelectors';
import {getUser} from '../../store/data/teammates/teammatesSelectors';
import {TimeComboBox} from '../common/dateTime/timeComboBox';
import {errorFallbackMessage} from '../errors/errorFallbackMessage';

/*
 * Style.
 */

const ToggleWrapperDiv = styled.div`
  display: flex;
  justify-content: space-between;
  padding-top: 14px;
  align-items: center;
`;

const ScheduleBlockDiv = styled.div`
  max-width: 240px;
  display: grid;
  grid-template-columns: 70px auto 70px auto;
  grid-auto-rows: auto;
  grid-column-gap: 4px;
`;

const StyledTimeComboBox = styled(TimeComboBox)`
  margin: 0;
  margin-top: 8px;
  width: 80px;
`;

const ScheduleBlockSeparatorSpan = styled.span`
  color: ${(p) => p.theme.greys.shade70};
  font-weight: ${fontWeights.semibold};
  font-size: ${fontSizes.small};
  ${center()};
  margin-top: 6px;
`;

/*
 * Component.
 */

export const MuteTeammateNotifications: FC = () => {
  const dispatch = useAppDispatch();
  const user = useAppSelector(getUser);
  const shouldMuteNotifications = useAppSelector(getShouldMuteNotifications);
  const doNotDisturb = useAppSelector(getDoNotDisturb);
  const {interval, onStartTimeChange, onEndTimeChange} = useDoNotDisturb();
  const validInterval = filterValidInterval(interval);
  const timeFormat = useAppSelector(getTimeFormat);

  const toggleMuteNotifications = (isEnabled: boolean) =>
    dispatch(updateShouldMuteNotificationsAction(isEnabled));

  const toggleScheduledMuteNotifications = (isEnabled: boolean) => {
    dispatch(
      updateDoNotDisturbAction({
        ...doNotDisturb,
        isEnabled,
      }),
    );
  };
  const isOutOfOffice = user.availabilityStatus === TeammateAvailabilityStatusesEnum.OUT_OF_OFFICE;

  return (
    <>
      <ToggleWrapperDiv>
        <Trans id="settingshome-availability-notifications-mute">Mute notifications</Trans>
        <Toggle
          onChange={toggleMuteNotifications}
          isChecked={shouldMuteNotifications || isOutOfOffice}
          isDisabled={isOutOfOffice}
          size={VisualSizesEnum.SMALL}
        />
      </ToggleWrapperDiv>
      <ToggleWrapperDiv>
        <Trans id="settingshome-availability-notifications-scheduled-mute">
          Scheduled mute notifications
        </Trans>
        <Toggle
          onChange={toggleScheduledMuteNotifications}
          isChecked={doNotDisturb.isEnabled}
          size={VisualSizesEnum.SMALL}
        />
      </ToggleWrapperDiv>
      {validInterval ? (
        <ScheduleBlockDiv>
          <StyledTimeComboBox
            timeFormat={timeFormat}
            selectedTime={validInterval.start}
            onTimeSelect={(time) => time && onStartTimeChange(time)}
          />
          <ScheduleBlockSeparatorSpan>
            <Trans id="settingshomeprofile-to">to</Trans>
          </ScheduleBlockSeparatorSpan>
          <StyledTimeComboBox
            timeFormat={timeFormat}
            selectedTime={validInterval.end}
            onTimeSelect={(time) => time && onEndTimeChange(time)}
          />
          <ScheduleBlockSeparatorSpan>{validInterval.start.offsetNameShort}</ScheduleBlockSeparatorSpan>
        </ScheduleBlockDiv>
      ) : (
        errorFallbackMessage
      )}
    </>
  );
};
